import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { getUserTitle } from "../../../User/UserProfile";
import { TOUCH_QUERY } from "../../../../utils/useViewport";
import { MetricGroup } from "../useMetrics";
import CoreMetricChart, { CoreMetric } from "./CoreMetricChart";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

interface ConsultantStatsRowProps {
  group: MetricGroup;
  /** Title and e-mail for the panel, where the consultant is a known user. */
  profile?: { role?: string; email?: string };
  metrics: CoreMetric[];
  wideLabels: boolean;
  onMetricClick: (metric: CoreMetric, group: MetricGroup) => void;
  onDetailReport: (group: MetricGroup) => void;
}

/**
 * One consultant, as the old Program Statistics page drew them: the magenta
 * profile panel beside their chart (above it below `md`), with the Detail
 * Report link underneath.
 */
const ConsultantStatsRow: React.FC<ConsultantStatsRowProps> = ({
  group,
  profile,
  metrics,
  wideLabels,
  onMetricClick,
  onDetailReport,
}) => (
  <Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        borderRadius: "16px",
        border: CARD_BORDER,
        backgroundColor: "white",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          // 20 % as on the old page, but never too narrow for an e-mail
          // domain on a small tablet-landscape row.
          width: { xs: "100%", md: "20%" },
          minWidth: { md: 200 },
          flexShrink: 0,
          backgroundColor: PRIMARY,
          p: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          textAlign: "center",
          overflowWrap: "anywhere",
        }}
      >
        {profile?.role && (
          <Typography variant="h6" sx={{ color: "#FDB814", fontSize: "12px" }}>
            {getUserTitle(profile)}
          </Typography>
        )}
        <Typography variant="h5" sx={{ color: "white" }}>
          {group.label}
        </Typography>
        {profile?.email && (
          <Typography variant="subtitle1" sx={{ color: "white" }}>
            {/* A long address breaks after the "@" before it breaks mid-word. */}
            {profile.email.split("@").map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <>
                    @<wbr />
                  </>
                )}
                {part}
              </React.Fragment>
            ))}
          </Typography>
        )}
      </Box>
      <Box sx={{ width: { xs: "100%", md: "auto" }, flex: { md: 1 }, minWidth: 0 }}>
        <CoreMetricChart
          metrics={metrics}
          bucket={group.metrics}
          wideLabels={wideLabels}
          onMetricClick={(metric) => onMetricClick(metric, group)}
        />
      </Box>
    </Box>
    <Box sx={{ textAlign: "right", pr: "12px", pt: "4px" }}>
      <Button
        size="small"
        onClick={() => onDetailReport(group)}
        sx={{
          color: PRIMARY,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "1rem",
          [`@media ${TOUCH_QUERY}`]: { minHeight: 40 },
        }}
      >
        Detail Report
      </Button>
    </Box>
  </Box>
);

export default React.memo(ConsultantStatsRow);
